import React from 'react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen pt-20 pb-16 px-6" data-testid="terms-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-light tracking-tight text-foreground mb-4">
            Terms of Use
          </h1>
          <p className="text-muted-foreground">Effective Date: March 2025 | Last Updated: March 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 space-y-8">
            <p className="text-muted-foreground leading-relaxed">
              Please read these Terms of Use carefully before using this website or booking any services. By accessing or using this website, you agree to be bound by these terms. If you do not agree, please discontinue use immediately.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              These Terms of Use govern your access to and use of the website, booking services, and any future e-commerce offerings made available through Psychotherapy with Anvita.
            </p>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">1. About This Website</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This website is operated by Anvita Sethi (hereinafter referred to as "the Practitioner," "we," "us," or "our"), a psychologist practising under the brand Psychotherapy with Anvita. The website serves the following purposes:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Providing information about the Practitioner's professional background, qualifications, and areas of expertise</li>
                <li>Enabling prospective and existing clients to book individual therapy sessions, group programmes, workshops, and related services</li>
                <li>Sharing educational content, articles, and resources related to mental health and psychological well-being</li>
                <li>In the future, offering digital or physical products and resources via an integrated e-commerce facility</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                The Practitioner operates in accordance with applicable Indian law, including the Mental Healthcare Act, 2017, and guidelines issued by the Rehabilitation Council of India (RCI) or any other applicable regulatory body.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">2. Eligibility</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">By using this website, you represent and warrant that:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>You are at least 18 years of age, or if below 18, that you have obtained the express consent of a parent or legal guardian</li>
                <li>You are legally competent to enter into a binding agreement</li>
                <li>You are accessing this website from within India, or if from outside India, you accept that Indian law governs these Terms</li>
                <li>The information you provide at the time of registration or booking is accurate, complete, and current</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Services for minors (individuals below 18 years) are subject to written parental or guardian consent prior to commencement of any sessions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">3. Nature of Services</h2>
              
              <h3 className="text-xl font-heading font-medium text-foreground mb-3">3.1 Professional Services</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Psychotherapy with Anvita offers psychological assessment, counselling, and psychotherapy services provided by Anvita Sethi. All services are subject to a separate Service Agreement and Informed Consent form, which must be reviewed and signed before the first session.
              </p>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">3.2 Not a Medical Emergency Service</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This website and the services offered herein are not designed to address psychiatric emergencies or crises. If you or someone you know is experiencing a mental health emergency, please contact the appropriate local emergency services without delay. The Practitioner does not provide crisis intervention or emergency mental health services.
              </p>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">3.3 No Therapeutic Relationship via Website</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Browsing this website, reading its content, or submitting a contact enquiry does not in itself establish a therapeutic or professional relationship. A therapeutic relationship is formally established only after the signing of a Service Agreement and Informed Consent document.
              </p>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">3.4 Informational Content</h3>
              <p className="text-muted-foreground leading-relaxed">
                Any articles, blog posts, videos, resources, or other content published on this website are intended for general informational and educational purposes only. They do not constitute professional psychological advice, diagnosis, or treatment. You should not rely on such content as a substitute for professional guidance tailored to your circumstances.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">4. Booking and Appointments</h2>
              
              <h3 className="text-xl font-heading font-medium text-foreground mb-3">4.1 Session Booking</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Appointments may be booked through the scheduling facility on this website. All bookings are subject to the Practitioner's availability. A booking confirmation does not by itself establish a therapeutic relationship.
              </p>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">4.2 Rescheduling and Cancellations</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Cancellations made at least 24 hours before the scheduled appointment are eligible for a full refund or rescheduling at no additional charge</li>
                <li>Cancellations made within 24 hours of the appointment are subject to a cancellation fee equivalent to 50% of the session fee</li>
                <li>No-shows (failure to attend without prior notice) will be charged the full session fee</li>
                <li>The Practitioner reserves the right to cancel or reschedule appointments due to unforeseen circumstances; in such cases, a full refund or an alternate slot will be offered</li>
              </ul>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">4.3 Fees, Currency, and Payment</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Session fees and applicable charges are displayed on the website at the time of booking. Payments are accepted as follows:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Within India:</strong> All fees are denominated in Indian Rupees (INR) and are payable via the available online payment gateway.</li>
                <li><strong>Outside India:</strong> Clients booking from outside India have the option to pay either in Indian Rupees (INR) via Razorpay, or in United States Dollars (USD) via PayPal.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">5. Confidentiality and Privacy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Practitioner is bound by professional ethical obligations of confidentiality. As a user of this website, please be aware:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Information submitted through the website's contact forms or booking system is processed in accordance with our Privacy Policy</li>
                <li>Confidentiality obligations specific to the therapeutic relationship are outlined in the Informed Consent document</li>
                <li>Confidentiality may be limited in circumstances required by law, including situations involving imminent risk of harm to self or others, child protection concerns, and court orders</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All content on this website — including text, graphics, logos, images, audio and video material, worksheets, and downloadable resources — is the exclusive intellectual property of Psychotherapy with Anvita or Anvita Sethi, and is protected under applicable Indian copyright and intellectual property laws.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                You are granted a limited, non-exclusive, non-transferable licence to access and view the content for personal, non-commercial use only.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">7. User Conduct</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">As a condition of using this website, you agree not to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Use the website for any unlawful purpose or in violation of any applicable law</li>
                <li>Submit false, misleading, or fraudulent information</li>
                <li>Attempt to gain unauthorised access to any part of the website or its related systems</li>
                <li>Transmit any harmful, offensive, or disruptive content via the website's communication channels</li>
                <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
                <li>Engage in any activity that could damage, disable, or impair the website's functionality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the fullest extent permitted by applicable law, Anvita Sethi and Psychotherapy with Anvita shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of or inability to use this website, reliance on any information, content, or resources published on the website, unauthorised access to or alteration of your data, or any interruption or cessation of services on the website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">9. Governing Law and Dispute Resolution</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms of Use shall be governed by and construed in accordance with the laws of the Republic of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Bengaluru, Karnataka.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">10. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have any questions, concerns, or feedback regarding these Terms of Use, please reach out to:
              </p>
              <div className="bg-secondary/50 rounded-xl p-4">
                <p className="text-foreground font-medium">Anvita Sethi</p>
                <p className="text-muted-foreground">Psychotherapy with Anvita</p>
                <p className="text-muted-foreground">Email: <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a></p>
              </div>
            </section>

            <div className="border-t border-border pt-8 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By using this website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
              </p>
              <p className="text-sm text-muted-foreground text-center mt-4">
                © 2025 Psychotherapy with Anvita. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
